import type { LocationQuery } from "./locationQuerySchema";

/**
 * Parse URL search params into LocationQuery object.
 *
 * @param search - URL search string (with or without leading '?')
 * @returns Parsed LocationQuery object
 *
 * @example
 * ```typescript
 * const query = parseLocationQuery("?locationId=loc-123");
 * // => { locationId: "loc-123" }
 *
 * const query2 = parseLocationQuery("locationId=loc-123");
 * // => { locationId: "loc-123" }
 * ```
 *
 * @remarks
 * - Handles search strings with or without leading '?'
 * - Returns undefined for missing or empty locationId
 * - No validation is performed on locationId format
 */
export function parseLocationQuery(search: string): LocationQuery {
  const normalizedSearch = search.startsWith("?") ? search.substring(1) : search;
  const params = new URLSearchParams(normalizedSearch);

  const locationId = params.get("locationId");

  return {
    locationId: locationId ?? undefined,
  };
}

/**
 * Convert LocationQuery object to URL search string.
 *
 * @param query - LocationQuery object
 * @returns URL search string without leading "?"
 *
 * @example
 * ```typescript
 * const search = stringifyLocationQuery({ locationId: "loc-123" });
 * // => "locationId=loc-123"
 *
 * const empty = stringifyLocationQuery({});
 * // => ""
 *
 * const withUndefined = stringifyLocationQuery({ locationId: undefined });
 * // => ""
 * ```
 *
 * @remarks
 * - Returns empty string if no valid parameters
 * - Undefined or null values are omitted
 * - Empty strings are omitted
 */
export function stringifyLocationQuery(query: LocationQuery): string {
  const params = new URLSearchParams();

  if (query.locationId) {
    params.set("locationId", query.locationId);
  }

  return params.toString();
}
